import { Form, Switch, Tooltip } from 'antd';
import React, { useEffect, useMemo } from 'react';

import { AddressPreview } from './MainAddressSection/AddressPreview';
import { AddressSelect } from './MainAddressSection/AddressSelect';
import { HouseTreeSelect } from './MainAddressSection/HouseTreeSelect';
import { ScheduleMonthPicker } from './MainAddressSection/ScheduleMonthPicker';
import styles from './MainAddressSection/MainAddressSection.module.scss';
import { FormSection, SectionHeader } from './FormSection';
import { workScheduleByPeriodFields } from '../CreateNewWorkScheduleByPeriod.constants';
import {
  getSelectedFirstOptions,
  validatePeriod,
} from './MainAddressSection/utils';

export const MainAddressSection = ({
  form,
  initialValues = {},
  canEditField = () => true,
  canUseInitialValue = () => true,
  allCompanyAddresses,
  cityOptions,
  cityDisabled,
  cityDisabledHint,
  cityRegionOptions,
  cityRegionDisabled,
  cityRegionDisabledHint,
  streetOptions,
  streetDisabled,
  streetDisabledHint,
  houseOptions,
  houseDisabled,
  houseDisabledHint,
  citiesLoading,
  cityRegionsLoading,
  streetsLoading,
  housesLoading,
  periodCheckLoading,
  periodCheckMessage,
  onAllCompanyAddressesChange,
  onPeriodChange,
}) => {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const periodEditable = canEditField(workScheduleByPeriodFields.period);
  const usePeriodInitialValue = canUseInitialValue(
    workScheduleByPeriodFields.period
  );
  const allCompanyAddressesEditable = canEditField(
    workScheduleByPeriodFields.allCompanyAddresses
  );
  const cityEditable = canEditField(workScheduleByPeriodFields.cityIds);
  const streetEditable = canEditField(workScheduleByPeriodFields.streetIds);
  const cityRegionEditable = canEditField(
    workScheduleByPeriodFields.cityRegionIds
  );
  const houseEditable = canEditField(workScheduleByPeriodFields.houseIds);
  const cityRegionIds =
    getFieldValue('cityRegionIds') || initialValues.cityRegionIds || [];
  const shouldValidateCity =
    !allCompanyAddresses && !cityDisabled && cityEditable;
  const shouldValidateStreet =
    !allCompanyAddresses &&
    !cityRegionIds.length &&
    !streetDisabled &&
    streetEditable;
  const shouldValidateHouse =
    !allCompanyAddresses &&
    !cityRegionIds.length &&
    !houseDisabled &&
    houseEditable;
  const cityIds = getFieldValue('cityIds') || initialValues.cityIds || [];
  const streetIds = getFieldValue('streetIds') || initialValues.streetIds || [];
  const houseIds = getFieldValue('houseIds') || initialValues.houseIds || [];
  const sortedCityOptions = useMemo(
    () => getSelectedFirstOptions(cityOptions, cityIds),
    [cityIds, cityOptions]
  );
  const sortedStreetOptions = useMemo(
    () => getSelectedFirstOptions(streetOptions, streetIds),
    [streetIds, streetOptions]
  );
  const sortedCityRegionOptions = useMemo(
    () => getSelectedFirstOptions(cityRegionOptions, cityRegionIds),
    [cityRegionIds, cityRegionOptions]
  );
  const sortedHouseOptions = useMemo(
    () => getSelectedFirstOptions(houseOptions, houseIds),
    [houseIds, houseOptions]
  );

  useEffect(() => {
    setFieldsValue({
      cityIds: initialValues.cityIds || [],
      cityRegionIds: initialValues.cityRegionIds || [],
      streetIds: initialValues.streetIds || [],
      houseIds: initialValues.houseIds || [],
    });
  }, [
    initialValues.cityIds,
    initialValues.cityRegionIds,
    initialValues.houseIds,
    initialValues.streetIds,
    setFieldsValue,
  ]);

  return (
    <FormSection title="Основные">
      <div className={styles.periodGrid}>
        <Form.Item
          label="Месяц графика"
          className={styles.formItem}
          validateStatus={
            periodCheckMessage
              ? 'error'
              : periodCheckLoading
              ? 'validating'
              : undefined
          }
          help={
            periodCheckMessage ||
            (periodCheckLoading
              ? 'Проверяем возможность создания графика...'
              : undefined)
          }
        >
          {getFieldDecorator('periodMonths', {
            initialValue: usePeriodInitialValue
              ? initialValues.periodMonths
              : undefined,
            rules: [{ validator: validatePeriod }],
          })(
            <ScheduleMonthPicker
              className={styles.periodPicker}
              disabled={!periodEditable}
              id="workSchedulePeriodMonths"
              name="periodMonths"
              onMonthChange={onPeriodChange}
            />
          )}
        </Form.Item>
      </div>
      <SectionHeader
        title="Адреса"
        actions={
          <div className={styles.switchRow}>
            <span>Все адреса УК</span>
            <Switch
              checked={allCompanyAddresses}
              disabled={!allCompanyAddressesEditable}
              onChange={onAllCompanyAddressesChange}
            />
          </div>
        }
      />
      <div className={styles.addressGrid}>
        <Tooltip
          title={
            cityEditable
              ? cityDisabledHint
              : 'Город нельзя изменить после создания графика по периоду'
          }
        >
          <Form.Item label="Город" className={styles.formItem}>
            {getFieldDecorator('cityIds', {
              initialValue: initialValues.cityIds || [],
              rules: shouldValidateCity
                ? [
                    {
                      type: 'array',
                      required: true,
                      message: 'Выберите город',
                    },
                  ]
                : [],
            })(
              <AddressSelect
                className={`${styles.dropdownSelect} ${styles.addressSelect}`}
                disabled={cityDisabled || !cityEditable}
                id="workScheduleCityIds"
                loading={citiesLoading}
                name="cityIds"
                options={sortedCityOptions}
                label="Город"
              />
            )}
          </Form.Item>
        </Tooltip>
        <Tooltip title={cityRegionDisabledHint}>
          <Form.Item label="Микрорайон" className={styles.formItem}>
            {getFieldDecorator('cityRegionIds', {
              initialValue: initialValues.cityRegionIds || [],
            })(
              <AddressSelect
                className={`${styles.dropdownSelect} ${styles.addressSelect}`}
                disabled={cityRegionDisabled || !cityRegionEditable}
                id="workScheduleCityRegionIds"
                loading={cityRegionsLoading}
                multiple
                name="cityRegionIds"
                options={sortedCityRegionOptions}
                label="Микрорайон"
              />
            )}
          </Form.Item>
        </Tooltip>
        <Tooltip title={streetDisabledHint}>
          <Form.Item label="Улица" className={styles.formItem}>
            {getFieldDecorator('streetIds', {
              initialValue: initialValues.streetIds || [],
              rules: shouldValidateStreet
                ? [
                    {
                      type: 'array',
                      required: true,
                      message: 'Выберите улицу',
                    },
                  ]
                : [],
            })(
              <AddressSelect
                className={`${styles.dropdownSelect} ${styles.addressSelect}`}
                disabled={streetDisabled || !streetEditable}
                id="workScheduleStreetIds"
                loading={streetsLoading}
                multiple
                name="streetIds"
                options={sortedStreetOptions}
                label="Улица"
              />
            )}
          </Form.Item>
        </Tooltip>
        <Tooltip title={houseDisabledHint}>
          <Form.Item label="Дом" className={styles.formItem}>
            {getFieldDecorator('houseIds', {
              initialValue: initialValues.houseIds || [],
              rules: shouldValidateHouse
                ? [
                    {
                      type: 'array',
                      required: true,
                      message: 'Выберите дом',
                    },
                  ]
                : [],
            })(
              <HouseTreeSelect
                className={`${styles.dropdownSelect} ${styles.addressSelect}`}
                disabled={houseDisabled || !houseEditable}
                id="workScheduleHouseIds"
                loading={housesLoading}
                name="houseIds"
                houseOptions={sortedHouseOptions}
                streetOptions={sortedStreetOptions}
              />
            )}
          </Form.Item>
        </Tooltip>
        <AddressPreview
          allCompanyAddresses={allCompanyAddresses}
          cityIds={cityIds}
          cityOptions={sortedCityOptions}
          cityRegionIds={cityRegionIds}
          cityRegionOptions={sortedCityRegionOptions}
          houseIds={houseIds}
          houseOptions={sortedHouseOptions}
          streetIds={streetIds}
          streetOptions={sortedStreetOptions}
        />
      </div>
    </FormSection>
  );
};
